import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { RootState } from '../../../redux/root';
import { setInspectorName } from '../../../redux/slices/inspectionsSlice';
import { dispatch } from '../../../redux/store';
import { Loader } from '../../../atoms';
import {
  useGetAllInspectorsQuery,
  useGetScheduledInspectionsQuery,
  useUpdateInspectionInspectorMutation,
} from '../../../redux/rtkQuery/apiSlice';
import { setAdditionlScheduledCount, setInspectorId, setScheduledCount } from '../../../redux/slices/bulkSlice';
import { getInspectionTypeByID } from '../../../api/inspectionTypes';
import { getTradesByProjectType } from '../../../api/inspections';
import { getEffectiveTradeIds, TradeRef } from '../../../utils/inspectorTradeQualification';

const BulkInspectorDropdown = ({ row }) => {
  const [params, setParams] = useState<any>();
  const { data = [], isFetching } = useGetAllInspectorsQuery(null);
  const [updateInspectionInspector, { isLoading }] = useUpdateInspectionInspectorMutation();
  const { data: counts } = useGetScheduledInspectionsQuery(params, { skip: params === undefined });
  const { bulkDate, inspectorId, bulkInspections } = useSelector((s: RootState) => s.bulkSlice);
  const tableSelectedRows = useSelector((s: RootState) => s.general.tableSelectedRows);
  const [inspectors, setInspectors] = useState<any>();

  // EA-1123 parity with EA-1195: qualifying an inspector must account for the
  // Residential-license expansion AND the project type (1&2 dwelling vs
  // multifamily/commercial). Bulk selections can span MULTIPLE projects, so
  // each selected inspection carries its own required trades + project type,
  // and an inspector must qualify for EVERY selected inspection.
  //
  // Per selected inspection we compute: { tradeIds, projectType }. The
  // getBulkPermits row does not carry trades (only the inspection type id and
  // projectType), so type trades are resolved via getInspectionTypeByID and the
  // project-type trade set via getTradesByProjectType — both cached.
  interface Requirement {
    tradeIds: number[];
    projectTypeTrades: TradeRef[];
  }
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const typeTradeCache = React.useRef<Record<number, number[]>>({});
  const projectTypeCache = React.useRef<Record<string, TradeRef[]>>({});

  // Extract the numeric inspection-type id from a bulk row. On the
  // getBulkPermits payload the type id lives in `inspectionType` (a number);
  // `typeName` is the display name and `type` is a record kind ('inspection').
  const getTypeId = (r: any): number | null => {
    const candidates = [r?.inspectionType, r?.inspectionTypeId, r?.typeId];
    const found = candidates.find(
      (c: any) => c !== null && c !== undefined && c !== '' && !Number.isNaN(Number(c)),
    );
    return found !== undefined && found !== null ? Number(found) : null;
  };

  const selectedIds = React.useMemo(
    () => (tableSelectedRows || []).filter((r: any) => r.selected).map((r: any) => r.id),
    [tableSelectedRows],
  );

  const resolveTypeTrades = (typeId: number): Promise<number[]> => {
    if (typeTradeCache.current[typeId]) {
      return Promise.resolve(typeTradeCache.current[typeId]);
    }
    return getInspectionTypeByID(typeId)
      .then((res: any) => {
        const raw = res?.data?.trades || res?.data?.tradeIds || [];
        const trades = (Array.isArray(raw) ? raw : []).map((t: any) =>
          Number(typeof t === 'object' && t !== null ? t.id : t),
        );
        typeTradeCache.current[typeId] = trades;
        return trades;
      })
      .catch(() => []);
  };

  const resolveProjectTypeTrades = (projectType: string): Promise<TradeRef[]> => {
    const key = projectType || '__none__';
    if (projectTypeCache.current[key]) {
      return Promise.resolve(projectTypeCache.current[key]);
    }
    return getTradesByProjectType(projectType || null)
      .then((res: any) => {
        const trades: TradeRef[] = (res?.data?.trades as TradeRef[]) || [];
        projectTypeCache.current[key] = trades;
        return trades;
      })
      .catch(() => []);
  };

  useEffect(() => {
    let cancelled = false;
    if (!selectedIds.length) {
      setRequirements([]);
      return undefined;
    }

    const rows = selectedIds
      .map((id: any) => (bulkInspections || []).find((b: any) => b.id === id))
      .filter(Boolean);

    Promise.all(
      rows.map(async (rowData: any) => {
        const typeId = getTypeId(rowData);
        const [tradeIds, projectTypeTrades] = await Promise.all([
          typeId ? resolveTypeTrades(typeId) : Promise.resolve([]),
          resolveProjectTypeTrades(rowData?.projectType),
        ]);
        return { tradeIds, projectTypeTrades } as Requirement;
      }),
    ).then((reqs: Requirement[]) => {
      if (cancelled) return;
      setRequirements(reqs);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedIds, bulkInspections]);

  // An inspector qualifies only if, for EVERY selected inspection, their
  // effective trades (Residential expanded against that inspection's
  // project-type trade set) intersect that inspection's trades AND the
  // project-type trade set. If nothing is selected or nothing resolved, show all.
  const qualifiedInspectors = React.useMemo(() => {
    if (!selectedIds.length || !requirements.length) return data;
    return data.filter((insp: any) =>
      requirements.every((req: Requirement) => {
        const inspectionSet = new Set<number>(req.tradeIds.map(Number));
        if (inspectionSet.size === 0) return true; // nothing to require
        const effIds = getEffectiveTradeIds(insp.trade, req.projectTypeTrades);
        if (req.projectTypeTrades && req.projectTypeTrades.length) {
          const projectSet = new Set<number>(req.projectTypeTrades.map(t => Number(t.id)));
          return effIds.some(id => inspectionSet.has(id) && projectSet.has(id));
        }
        return effIds.some(id => inspectionSet.has(id));
      }),
    );
  }, [data, selectedIds, requirements]);

  useEffect(() => {
    if (qualifiedInspectors) {
      setInspectors(qualifiedInspectors);
    }
  }, [qualifiedInspectors]);
  useEffect(() => {
    if (counts && inspectors) {
      const inspector = inspectors.find(i => i.value === inspectorId);
      const selectedRows = tableSelectedRows.filter(r => r.selected);
      const inspectionsWithThisInspector = bulkInspections.filter(b => b.inspector === inspector.label);
      let countOfSelectedRowsWithoutInspector = 0;
      selectedRows.forEach(row => {
        const alreadyBelongToInspector = inspectionsWithThisInspector.find(i => i.id === row.id);
        if (!alreadyBelongToInspector) {
          countOfSelectedRowsWithoutInspector++;
        }
      });
      dispatch(setAdditionlScheduledCount(countOfSelectedRowsWithoutInspector));
      dispatch(setScheduledCount(counts.scheduledCount));
    }
  }, [counts, inspectors]);
  useEffect(() => {
    if (inspectorId !== -1 && bulkDate) {
      const _params = {
        inspectorId,
        inspection_date: format(new Date(bulkDate), 'yyyy-MM-dd'),
      };
      setParams(_params);
    }
  }, [inspectorId, bulkDate]);
  const getInspectorName = id => {
    let selected = data.find(d => row.inspectorName === d.label);
    if (id) {
      selected = data.find(d => Number(d.value) === Number(id));
      if (selected) {
        return selected.label;
      }
    }
    if (selected) {
      return selected.value;
    }
    return '';
  };
  const onChange = async e => {
    const id = e.target.value;
    dispatch(setInspectorId(id));
    dispatch(setInspectorName(getInspectorName(id)));
  };
  return (
    <Wrapper>
      <SelectShell>
        <StyledSelect value={inspectorId} onChange={onChange}>
          <option value={-1}>Select inspector…</option>
          {qualifiedInspectors.map((item, index) => (
            <option key={index} value={item.value}>
              {item.label}
            </option>
          ))}
        </StyledSelect>
        <Caret aria-hidden>▾</Caret>
      </SelectShell>
      {(isFetching || isLoading) && (
        <LoaderWrapper>
          <Loader size={18} thickness={4} />
        </LoaderWrapper>
      )}
    </Wrapper>
  );
};

export default BulkInspectorDropdown;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const SelectShell = styled.div`
  position: relative;
  flex: 1;
  max-width: 320px;
`;

const StyledSelect = styled.select`
  width: 100%;
  height: 40px;
  padding: 0 36px 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-family: ${FONT_STACK};
  font-size: 0.875rem;
  color: var(--mainText);
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--neutral-400);
  }
  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
  &:disabled {
    background: var(--neutral-100);
    color: var(--neutral-500);
    cursor: not-allowed;
  }
`;

const Caret = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--neutral-500);
  pointer-events: none;
  font-size: 0.875rem;
`;

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
